import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import StreakReward from './StreakReward';
import StreakBar from './StreakBar';

// Reward the player with a mini-game break every Nth correct answer in a row.
// Kid mode (Paridhi) gets quicker rewards; older players (Rachit) earn it over a longer run.
const STREAK_REWARD_INTERVAL_KID = 3;
const STREAK_REWARD_INTERVAL_OLDER = 5;

// ── TYPES ────────────────────────────────────────────────────────────────────

export interface DifficultyLevel {
  label: string;
  desc?: string;
}

export interface PlayerConfig {
  playerName: string;
  localStorageKey: string;
  questionTypes: string[];
  typeInstructions?: Record<string, string>;
  difficultyConfig: Record<number, DifficultyLevel>;
  personaPrompt?: string;
  specialRules?: string;
  buildPrompt?: (history: string[], typeIndex: number, difficulty: number) => string;
  pageTitle: string;
}

interface QuestionData {
  question: string;
  emoji: string;
  type: string;
  answers: string[];
  correct: number;
  praise: string;
  hint: string;
  wrongContext: string[];
}

interface GameState {
  score: number;
  streak: number;
  bestStreak: number;
  total: number;
  typeIndex: number;
  themeIndex: number;
  categoryScores: Record<string, { correct: number; total: number }>;
  difficulty: number;
  gameTokens: number; // banked game-plays earned from completed streaks
  rewardsEarned: number; // lifetime tokens earned — drives which game shows next
}

interface CelebParticle {
  id: number;
  emoji: string;
  x: number;
  delay: number;
}

// ── CONSTANTS ─────────────────────────────────────────────────────────────────

const THEMES = [
  { bg: '#FFF8F0', card: '#FFF0D8', accent: '#E8720C', btn: '#FF8C1A', text: '#2D1200', light: '#FFD9A0' },
  { bg: '#F0F8FF', card: '#D8EEFF', accent: '#0B6BC9', btn: '#1A7FE8', text: '#001830', light: '#A0CCFF' },
  { bg: '#F2FFF0', card: '#D8FFD4', accent: '#1A9E2C', btn: '#22B833', text: '#002808', light: '#A0FFAA' },
  { bg: '#FFF0FA', card: '#FFD8F4', accent: '#C4178A', btn: '#E020A0', text: '#2D0020', light: '#FFA0E0' },
  { bg: '#F6F0FF', card: '#E8D8FF', accent: '#6B12CC', btn: '#7D20E8', text: '#180030', light: '#CCA0FF' },
  { bg: '#FFFFF0', card: '#FFFBD4', accent: '#A07800', btn: '#C09000', text: '#2A2000', light: '#FFE880' },
];

const TYPE_LABELS: Record<string, string> = {
  // Rachit
  math_multiplication: '✖️ Multiplication',
  math_word_problem_easy: '📖 Word Problem',
  science_animals: '🐾 Animal Science',
  vocabulary_grade4: '📚 Vocabulary',
  math_patterns: '🔢 Patterns',
  fun_space_facts: '🚀 Space',
  math_division_simple: '➗ Division',
  geography_usa_world: '🌍 Geography',
  mythology_legends: '⚡ Mythology',
  math_measurement_time: '⏰ Measurement',
  language_grammar: '✍️ Grammar',
  fun_lego_inventions: '🧱 LEGO & Inventions',
  // Paridhi
  'letter-recognition': '🔤 Letters',
  counting: '🔢 Counting',
  colors: '🎨 Colors',
  shapes: '🔷 Shapes',
  animals: '🐾 Animals',
  opposites: '↔️ Opposites',
  rhyming: '🎵 Rhyming',
  'beginning-sound': '🔊 Sounds',
  'simple-math': '➕ Math',
  patterns: '🔁 Patterns',
};

const CELEBRATIONS = ['🎉', '⭐', '🌟', '✨', '🎊', '🏆', '💫', '🥳', '🔥', '💥'];

const SEED_TOPICS = [
  'underwater caves', 'ancient Egypt', 'volcanoes', 'rainforest insects', 'Arctic exploration',
  'medieval castles', 'robot engineers', 'deep sea creatures', 'the Amazon river', 'desert survival',
  'Mars colonies', 'bioluminescent animals', 'shipwrecks', 'the Silk Road', 'lightning storms',
  'ninja history', 'giant squid', 'the ISS', 'Viking ships', 'carnivorous plants',
  'hummingbirds', 'coral reefs', 'sand dunes', 'aurora borealis', 'komodo dragons',
  'the Great Wall', 'meteor showers', 'tide pools', 'cave paintings', 'electric eels',
];

// INTEGRATION FIX: Worker expects seed as an integer index (used in pickSeed(seedIndex % SEED_TOPICS.length)).
// Previously returned a string topic name, which failed Number.isInteger() and caused the Worker to
// silently fall back to its own random seed, discarding the client's value. Now returns an integer index.
function randSeed() {
  return Math.floor(Math.random() * SEED_TOPICS.length);
}

const INIT_GAME_STATE: GameState = {
  score: 0,
  streak: 0,
  bestStreak: 0,
  total: 0,
  typeIndex: 0,
  themeIndex: 0,
  categoryScores: {},
  difficulty: 0,
  gameTokens: 0,
  rewardsEarned: 0,
};

// ── TEXT-TO-SPEECH ───────────────────────────────────────────────────────────

// Pre-load voices as early as possible
if ('speechSynthesis' in window) {
  window.speechSynthesis.getVoices();
  window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
}

function speakText(text: string) {
  if (!('speechSynthesis' in window)) return;
  const synth = window.speechSynthesis;
  const clean = text.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim();
  if (!clean) return;
  synth.cancel();
  const utter = new SpeechSynthesisUtterance(clean);
  const voices = synth.getVoices();
  const voice = voices.find(v => v.localService && v.lang === 'en-US')
    || voices.find(v => v.localService && v.lang.startsWith('en'))
    || voices.find(v => v.lang.startsWith('en'));
  if (voice) utter.voice = voice;
  utter.rate = 0.85;
  utter.pitch = 1.1;
  synth.speak(utter);
  // Chrome bug: synthesis queue can enter a paused state silently — resume() unsticks it
  synth.resume();
}

function SpeakButton({ text, size = '1.2rem' }: { text: string; size?: string }) {
  return (
    <span
      role="button"
      aria-label="Read aloud"
      onMouseDown={(e) => { e.stopPropagation(); }}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); speakText(text); }}
      style={{
        cursor: 'pointer',
        fontSize: size,
        padding: '4px 8px',
        userSelect: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
      }}
    >
      🔊
    </span>
  );
}


// ── COMPONENT ─────────────────────────────────────────────────────────────────

interface QuizGameProps {
  config: PlayerConfig;
}

export default function QuizGame({ config }: QuizGameProps) {
  const [gs, setGs] = useState<GameState>(INIT_GAME_STATE);
  const [q, setQ] = useState<QuestionData | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cels, setCels] = useState<CelebParticle[]>([]);
  const [showHint, setShowHint] = useState(false);
  const [shakIdx, setShakIdx] = useState<number | null>(null);
  const [showWrongCtx, setShowWrongCtx] = useState(false);
  const [resetStep, setResetStep] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [forceGameId, setForceGameId] = useState<string | null>(null); // testing backdoor
  const [menuOpen, setMenuOpen] = useState(false); // settings menu (difficulty + New Day)
  const menuRef = useRef<HTMLDivElement>(null);

  const isKid = config.playerName.toLowerCase() === 'paridhi';
  const theme = THEMES[gs.themeIndex % THEMES.length];

  // ── testing backdoor ──────────────────────────────────────────────────────
  // Open the reward overlay directly via URL, e.g. ?game=whack-a-mole (specific
  // game) or ?game=test (picker). Lets the parent preview games without grinding
  // a streak. The picker inside the overlay can switch between all games.
  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('game');
    if (param) {
      setForceGameId(param);
      setRewardOpen(true);
    }
  }, []);

  // close the settings menu on any outside click / tap
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setResetStep(0);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [menuOpen]);

  const renderQuestionText = (text: string, emojiBlockStyle: React.CSSProperties) => {
    if (!isKid) return text;
    const emojiRegex = /((?:[\p{Emoji_Presentation}\p{Extended_Pictographic}][️]?[‍]?)+)/gu;
    const parts = text.split(emojiRegex);
    return parts.map((part, i) => {
      emojiRegex.lastIndex = 0;
      if (!emojiRegex.test(part)) { emojiRegex.lastIndex = 0; return <span key={i}>{part}</span>; }
      emojiRegex.lastIndex = 0;
      const emojiCount = [...part.matchAll(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu)].length;
      if (emojiCount >= 2) {
        return <span key={i} style={emojiBlockStyle}>{part}</span>;
      }
      return <span key={i} style={{ fontSize: emojiBlockStyle.fontSize }}>{part}</span>;
    });
  };

  // ── localStorage restore on mount ────────────────────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem(config.localStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as GameState;
        if (typeof parsed.score === 'number' && typeof parsed.total === 'number') {
          // backfill fields added after this state was first saved
          setGs({ ...INIT_GAME_STATE, ...parsed });
        }
      }
    } catch {
      // corrupted — ignore, use INIT
    }
    setMounted(true);
  }, [config.localStorageKey]);

  // ── fetch question ────────────────────────────────────────────────────────
  const fetchQ = useCallback(async (state: GameState) => {
    setLoading(true);
    setErr(null);
    setSel(null);
    setRevealed(false);
    setShowHint(false);
    setShakIdx(null);
    setShowWrongCtx(false);

    const workerUrl = process.env.REACT_APP_QUIZ_WORKER_URL;
    if (!workerUrl) {
      setErr('Quiz service URL not configured.');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(workerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player: config.playerName.toLowerCase(),
          typeIndex: state.typeIndex,
          difficulty: state.difficulty,
          seed: randSeed(),
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data: QuestionData = await res.json();
      setQ(data);
    } catch {
      setErr("Oops! Couldn't load a question. Tap Retry!");
    } finally {
      setLoading(false);
    }
  }, [config.playerName]);

  // fetch first question once mounted and state is restored
  useEffect(() => {
    if (mounted) {
      fetchQ(gs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  // ── handle answer ─────────────────────────────────────────────────────────
  const handleAnswer = (idx: number) => {
    if (revealed || loading || !q) return;
    setSel(idx);
    setRevealed(true);

    const correct = idx === q.correct;

    if (!correct) {
      setShakIdx(idx);
      setTimeout(() => setShakIdx(null), 600);
    } else {
      const items: CelebParticle[] = Array.from({ length: 7 }, (_, i) => ({
        id: Date.now() + i,
        emoji: CELEBRATIONS[Math.floor(Math.random() * CELEBRATIONS.length)],
        x: 5 + Math.random() * 90,
        delay: i * 0.12,
      }));
      setCels(items);
      setTimeout(() => setCels([]), 2200);
    }

    setGs(prev => {
      const newStreak = correct ? prev.streak + 1 : 0;
      const cat = q.type;
      const cs = { ...prev.categoryScores };
      if (!cs[cat]) cs[cat] = { correct: 0, total: 0 };
      cs[cat] = {
        correct: cs[cat].correct + (correct ? 1 : 0),
        total: cs[cat].total + 1,
      };

      // Completed a streak? Bank a game token (the orb glows; kid chooses when
      // to play). Wrong answers reset the charge ring but never burn tokens.
      const interval = isKid ? STREAK_REWARD_INTERVAL_KID : STREAK_REWARD_INTERVAL_OLDER;
      const earnedToken = correct && newStreak > 0 && newStreak % interval === 0;

      const next: GameState = {
        ...prev,
        score: prev.score + (correct ? 1 : 0),
        streak: newStreak,
        bestStreak: Math.max(prev.bestStreak, newStreak),
        total: prev.total + 1,
        typeIndex: prev.typeIndex + 1,
        categoryScores: cs,
        gameTokens: prev.gameTokens + (earnedToken ? 1 : 0),
        rewardsEarned: prev.rewardsEarned + (earnedToken ? 1 : 0),
      };

      // auto-save
      try {
        localStorage.setItem(config.localStorageKey, JSON.stringify(next));
      } catch {
        // storage full — ignore
      }

      return next;
    });
  };

  const handleNext = () => {
    setGs(prev => {
      const next = { ...prev, themeIndex: prev.themeIndex + 1 };
      fetchQ(next);
      return next;
    });
  };

  // Tap the charged orb: spend one banked token and open the game.
  const handlePlayGame = () => {
    if (gs.gameTokens <= 0) return;
    setForceGameId(null);
    setGs(prev => {
      const next = { ...prev, gameTokens: Math.max(0, prev.gameTokens - 1) };
      try {
        localStorage.setItem(config.localStorageKey, JSON.stringify(next));
      } catch {}
      return next;
    });
    setRewardOpen(true);
  };

  const handleDifficultyChange = (d: number) => {
    setGs(prev => {
      const next = { ...prev, difficulty: d };
      try {
        localStorage.setItem(config.localStorageKey, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const handleNewDay = () => {
    if (resetStep === 0) {
      setResetStep(1);
      setTimeout(() => setResetStep(0), 3000);
      return;
    }
    localStorage.removeItem(config.localStorageKey);
    const fresh = { ...INIT_GAME_STATE };
    setGs(fresh);
    setResetStep(0);
    setQ(null);
    setSel(null);
    setRevealed(false);
    setErr(null);
    fetchQ(fresh);
  };

  const accuracy = gs.total > 0 ? Math.round((gs.score / gs.total) * 100) : 0;
  const currentType = config.questionTypes[gs.typeIndex % config.questionTypes.length];
  const typeLabel = TYPE_LABELS[currentType] || currentType;

  // Streak orb: how far the current streak has charged toward the next token.
  const rewardInterval = isKid ? STREAK_REWARD_INTERVAL_KID : STREAK_REWARD_INTERVAL_OLDER;
  const streakCharge = gs.streak % rewardInterval;

  // ── STYLES ────────────────────────────────────────────────────────────────

  const styles = {
    page: {
      minHeight: '100vh',
      background: theme.bg,
      color: theme.text,
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      padding: '0',
      margin: '0',
    } as React.CSSProperties,

    inner: {
      maxWidth: '640px',
      margin: '0 auto',
      padding: '12px 16px 32px',
    } as React.CSSProperties,

    toolbar: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '8px',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '12px',
    } as React.CSSProperties,

    statsRow: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '8px',
      alignItems: 'center',
    } as React.CSSProperties,

    statChip: {
      background: theme.card,
      border: `1.5px solid ${theme.light}`,
      borderRadius: '20px',
      padding: '4px 12px',
      fontSize: '0.85rem',
      fontWeight: 600,
    } as React.CSSProperties,

    diffBtn: (active: boolean) => ({
      padding: '8px 14px',
      borderRadius: '12px',
      border: `2px solid ${active ? theme.accent : theme.light}`,
      background: active ? theme.accent : theme.card,
      color: active ? '#fff' : theme.text,
      fontWeight: 600,
      fontSize: '0.82rem',
      cursor: 'pointer',
      minHeight: '40px',
      width: '100%',
      textAlign: 'left' as const,
    } as React.CSSProperties),

    gearBtn: {
      height: isKid ? '32px' : '30px',
      minWidth: isKid ? '32px' : '30px',
      padding: '0 8px',
      borderRadius: '16px',
      background: 'transparent',
      color: theme.accent,
      fontSize: '0.95rem',
      border: `1.5px solid ${theme.accent}`,
      cursor: 'pointer',
      whiteSpace: 'nowrap' as const,
    } as React.CSSProperties,

    menuPanel: {
      position: 'absolute' as const,
      top: 'calc(100% + 6px)',
      right: 0,
      zIndex: 50,
      background: theme.card,
      border: `2px solid ${theme.light}`,
      borderRadius: '14px',
      boxShadow: `0 8px 24px ${theme.text}33`,
      padding: '12px',
      minWidth: '200px',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '6px',
    } as React.CSSProperties,

    menuLabel: {
      fontSize: '0.72rem',
      fontWeight: 700,
      opacity: 0.6,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.04em',
      margin: '2px 2px 4px',
    } as React.CSSProperties,

    card: {
      background: theme.card,
      borderRadius: '16px',
      padding: '20px',
      marginBottom: '14px',
      border: `2px solid ${theme.light}`,
      boxShadow: `0 4px 16px ${theme.light}55`,
    } as React.CSSProperties,

    questionText: {
      fontSize: isKid ? 'clamp(1.4rem, 5vw, 1.8rem)' : 'clamp(1.15rem, 3.5vw, 1.3rem)',
      fontWeight: 700,
      lineHeight: 1.5,
      marginBottom: '6px',
    } as React.CSSProperties,

    emojiBlock: {
      display: 'inline-block',
      background: '#1a1a2e',
      borderRadius: '12px',
      padding: '8px 14px',
      margin: '4px 0 8px',
      lineHeight: 1.6,
      fontSize: isKid ? 'clamp(1.8rem, 7vw, 2.5rem)' : '1.3rem',
      letterSpacing: '2px',
    } as React.CSSProperties,

    typeTag: {
      display: 'inline-block',
      background: theme.light,
      borderRadius: '12px',
      padding: '2px 10px',
      fontSize: '0.78rem',
      fontWeight: 600,
      marginBottom: '14px',
    } as React.CSSProperties,

    answerGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '10px',
    } as React.CSSProperties,

    answerBtn: (idx: number) => {
      let bg = theme.card;
      let border = `2px solid ${theme.light}`;
      let color = theme.text;

      if (revealed) {
        if (idx === q?.correct) {
          bg = '#22c55e22';
          border = '2px solid #22c55e';
          color = '#166534';
        } else if (idx === sel) {
          bg = '#ef444422';
          border = '2px solid #ef4444';
          color = '#991b1b';
        }
      }

      const shake = shakIdx === idx;

      return {
        padding: isKid ? '16px 18px' : '14px 16px',
        borderRadius: '12px',
        border,
        background: bg,
        color,
        fontWeight: 600,
        fontSize: isKid ? 'clamp(1.3rem, 5vw, 1.7rem)' : 'clamp(0.9rem, 2.8vw, 1rem)',
        cursor: revealed ? 'default' : 'pointer',
        textAlign: 'left' as const,
        minHeight: isKid ? '60px' : '52px',
        lineHeight: 1.3,
        transition: 'transform 0.08s ease, box-shadow 0.08s ease',
        animation: shake ? 'shake 0.5s ease' : 'none',
        width: '100%',
      } as React.CSSProperties;
    },

    primaryBtn: {
      padding: '14px 28px',
      borderRadius: '14px',
      background: theme.btn,
      color: '#fff',
      fontWeight: 700,
      fontSize: '1rem',
      border: 'none',
      cursor: 'pointer',
      minHeight: '50px',
      width: '100%',
    } as React.CSSProperties,

    outlineBtn: {
      padding: '10px 18px',
      borderRadius: '12px',
      background: 'transparent',
      color: theme.accent,
      fontWeight: 600,
      fontSize: '0.875rem',
      border: `2px solid ${theme.accent}`,
      cursor: 'pointer',
      minHeight: '44px',
    } as React.CSSProperties,

    hintBox: {
      background: theme.light + '55',
      borderLeft: `4px solid ${theme.accent}`,
      borderRadius: '0 8px 8px 0',
      padding: '10px 14px',
      fontSize: '0.9rem',
      marginBottom: '12px',
    } as React.CSSProperties,

    praiseBox: {
      background: '#22c55e15',
      borderLeft: '4px solid #22c55e',
      borderRadius: '0 8px 8px 0',
      padding: '10px 14px',
      fontSize: '0.9rem',
      color: '#166534',
      fontWeight: 600,
      marginBottom: '12px',
    } as React.CSSProperties,

    wrongCtxBox: {
      background: '#ef444415',
      borderLeft: '4px solid #ef4444',
      borderRadius: '0 8px 8px 0',
      padding: '10px 14px',
      fontSize: '0.88rem',
      color: '#7f1d1d',
      marginBottom: '12px',
    } as React.CSSProperties,

    newDayBtn: (step: number) => ({
      padding: '0 14px',
      borderRadius: '16px',
      background: step === 1 ? '#ef444415' : 'transparent',
      color: step === 1 ? '#ef4444' : theme.accent,
      fontWeight: 600,
      fontSize: '0.82rem',
      border: `1.5px solid ${step === 1 ? '#ef4444' : theme.accent}`,
      cursor: 'pointer',
      height: isKid ? '32px' : '30px', // match the inline streak bar height
      whiteSpace: 'nowrap' as const,
    } as React.CSSProperties),

    particle: (p: CelebParticle) => ({
      position: 'fixed' as const,
      top: '-20px',
      left: `${p.x}%`,
      fontSize: '2rem',
      animation: `fall 2s ease-in forwards`,
      animationDelay: `${p.delay}s`,
      zIndex: 9999,
      pointerEvents: 'none' as const,
    } as React.CSSProperties),
  };

  // ── RENDER ────────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet>
        <title>{config.pageTitle}</title>
      </Helmet>

      <style>{`
        @keyframes fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        @keyframes pulse-once {
          0% { transform: scale(1); }
          50% { transform: scale(1.04); }
          100% { transform: scale(1); }
        }
      `}</style>

      {/* Celebration particles */}
      {cels.map(p => (
        <div key={p.id} style={styles.particle(p)}>{p.emoji}</div>
      ))}

      {/* Streak reward mini-game break */}
      {rewardOpen && (
        <StreakReward
          theme={theme}
          isKid={isKid}
          rotation={gs.rewardsEarned}
          forceGameId={forceGameId}
          onClose={() => setRewardOpen(false)}
        />
      )}

      <div style={styles.page}>
        <div style={styles.inner}>

          {/* Toolbar — stats, inline streak bar, and New Day */}
          <div style={styles.toolbar}>
            <div style={styles.statsRow}>
              <span style={styles.statChip}>⭐ {gs.score}/{gs.total}</span>
              {gs.total > 0 && (
                <span style={styles.statChip}>{accuracy}% acc</span>
              )}
              {gs.bestStreak > 2 && (
                <span style={styles.statChip}>🏆 best {gs.bestStreak}</span>
              )}
            </div>

            {/* Streak bar — charges with each correct answer, becomes Play button when full */}
            <StreakBar
              theme={theme}
              charge={streakCharge}
              interval={rewardInterval}
              tokens={gs.gameTokens}
              isKid={isKid}
              onPlay={handlePlayGame}
            />

            {/* Settings menu — difficulty + New Day, folded behind a gear */}
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button
                style={styles.gearBtn}
                onClick={() => { setMenuOpen(o => !o); setResetStep(0); }}
                aria-label="Settings"
                aria-expanded={menuOpen}
              >
                ⚙️
              </button>

              {menuOpen && (
                <div style={styles.menuPanel}>
                  {Object.keys(config.difficultyConfig).length > 1 && (
                    <>
                      <div style={styles.menuLabel}>Difficulty</div>
                      {Object.entries(config.difficultyConfig).map(([key, cfg]) => {
                        const k = Number(key);
                        return (
                          <button
                            key={k}
                            style={styles.diffBtn(gs.difficulty === k)}
                            onClick={() => { handleDifficultyChange(k); setMenuOpen(false); }}
                          >
                            {gs.difficulty === k ? '✓ ' : ''}{cfg.label}
                          </button>
                        );
                      })}
                    </>
                  )}

                  <div style={{ ...styles.menuLabel, marginTop: '6px' }}>Reset</div>
                  <button
                    style={{ ...styles.newDayBtn(resetStep), width: '100%', textAlign: 'left' }}
                    onClick={handleNewDay}
                  >
                    {resetStep === 1 ? '⚠️ Tap again to confirm' : '🔄 New Day'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Question card */}
          <div style={styles.card}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px', animation: 'pulse-once 1s infinite' }}>
                  🤔
                </div>
                <div style={{ fontWeight: 600, color: theme.accent }}>
                  Generating question…
                </div>
              </div>
            ) : err ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>😬</div>
                <div style={{ color: '#ef4444', fontWeight: 600, marginBottom: '16px' }}>{err}</div>
                <button style={styles.primaryBtn} onClick={() => fetchQ(gs)}>
                  🔁 Retry
                </button>
              </div>
            ) : q ? (
              <>
                <div style={styles.typeTag}>{typeLabel}</div>
                <div style={styles.questionText}>
                  {isKid
                    ? <>{renderQuestionText(q.question, styles.emojiBlock)} <SpeakButton text={q.question} size="1.4rem" /></>
                    : <>{q.emoji} {q.question}</>
                  }
                </div>

                {/* Hint toggle */}
                {!revealed && (
                  <button
                    style={{ ...styles.outlineBtn, marginBottom: '12px', fontSize: '0.8rem', padding: '6px 14px', minHeight: '36px' }}
                    onClick={() => setShowHint(h => !h)}
                  >
                    {showHint ? '🙈 Hide Hint' : '💡 Hint'}
                  </button>
                )}

                {showHint && q.hint && (
                  <div style={styles.hintBox}>💡 {q.hint}</div>
                )}

                {/* Answer buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {q.answers.map((ans, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {isKid && <SpeakButton text={ans} size="1.3rem" />}
                      <button
                        style={{ ...styles.answerBtn(idx), flex: 1 }}
                        onClick={() => handleAnswer(idx)}
                        disabled={revealed}
                      >
                        <span style={{ marginRight: '6px', opacity: 0.6 }}>
                          {['A', 'B', 'C', 'D'][idx]}.
                        </span>
                        {isKid && /^[\p{Emoji_Presentation}\p{Extended_Pictographic}️‍\s]+$/u.test(ans)
                          ? <span style={{ background: '#1a1a2e', borderRadius: '8px', padding: '2px 8px' }}>{ans}</span>
                          : ans
                        }
                      </button>
                    </div>
                  ))}
                </div>

                {/* Post-answer feedback */}
                {revealed && (
                  <div style={{ marginTop: '14px' }}>
                    {sel === q.correct ? (
                      <div style={styles.praiseBox}>
                        ✅ {q.praise}
                      </div>
                    ) : (
                      <>
                        <div style={{ ...styles.praiseBox, background: '#ef444415', borderColor: '#ef4444', color: '#7f1d1d' }}>
                          The correct answer was: <strong>{q.answers[q.correct]}</strong>
                        </div>
                        {q.wrongContext && q.wrongContext[sel!] && (
                          <button
                            style={{ ...styles.outlineBtn, marginBottom: '10px', fontSize: '0.8rem', padding: '6px 14px', minHeight: '36px' }}
                            onClick={() => setShowWrongCtx(w => !w)}
                          >
                            {showWrongCtx ? '🙈 Hide Explanation' : '📖 Why was I wrong?'}
                          </button>
                        )}
                        {showWrongCtx && sel !== null && q.wrongContext?.[sel] && (
                          <div style={styles.wrongCtxBox}>
                            📚 {q.wrongContext[sel]}
                          </div>
                        )}
                      </>
                    )}

                    <button style={styles.primaryBtn} onClick={handleNext}>
                      Next Question →
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </div>

          {/* Category breakdown */}
          {Object.keys(gs.categoryScores).length > 0 && (
            <div style={{ ...styles.card, marginTop: '8px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '10px', opacity: 0.7 }}>
                📊 By Category
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {Object.entries(gs.categoryScores).map(([cat, data]) => {
                  const pct = Math.round((data.correct / data.total) * 100);
                  const label = TYPE_LABELS[cat] || cat;
                  const color = pct >= 70 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';
                  return (
                    <div
                      key={cat}
                      style={{
                        background: theme.card,
                        border: `1.5px solid ${color}55`,
                        borderRadius: '10px',
                        padding: '4px 10px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color,
                      }}
                    >
                      {label} {data.correct}/{data.total}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
